import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { LogoText } from '../ui/Logo';
import PortalButton from '../PortalButton';
import DashboardSpaceSwitch from '../DashboardSpaceSwitch';
import WorkspaceAmbientBackground from './WorkspaceAmbientBackground';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  rememberWorkspaceSection,
  type WorkspaceSection,
} from '@/lib/workspaceSection';

interface MemoryWorkspaceLayoutProps {
  children: ReactNode;
  onCreateSpace?: () => void;
  onManageSpaces?: () => void;
  /** Subtle radial tint from the active space color (hex) */
  workspaceAccentColor?: string | null;
  /** Default list layout width; map uses full viewport under header */
  mainVariant?: 'constrained' | 'fullBleed';
  /** Optional section sub-navigation shown under the global workspace header */
  sectionNav?: {
    current: WorkspaceSection;
    memoriesHref: string;
    mapHref: string;
    showMap: boolean;
  };
}

export default function MemoryWorkspaceLayout({
  children,
  onCreateSpace,
  onManageSpaces,
  workspaceAccentColor = null,
  mainVariant = 'constrained',
  sectionNav,
}: MemoryWorkspaceLayoutProps) {
  const { t } = useTranslation();

  const mainClassName =
    mainVariant === 'fullBleed'
      ? 'w-full min-h-[calc(100vh-4.5rem)] px-3 py-2 sm:px-4 sm:py-3'
      : 'max-w-6xl mx-auto px-6 lg:px-8 pt-2 pb-6';

  return (
    <div className="min-h-screen bg-workspace-surface-50 font-workspace text-workspace-text-primary relative">
      <WorkspaceAmbientBackground accentColor={workspaceAccentColor} />

      <div className="relative z-20">
        <header className="bg-workspace-surface-100/90 border-b border-workspace-surface-200/80 backdrop-blur-lg shadow-sm relative z-30">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="flex min-h-16 items-center justify-between gap-4 py-2">
              <div className="flex min-w-0 items-center gap-x-3">
                <LogoText
                  variant="dark"
                  size="md"
                  iconSize={32}
                  className="text-gray-900"
                />
                <DashboardSpaceSwitch
                  variant="workspace"
                  onCreateSpace={onCreateSpace}
                  onManageSpaces={onManageSpaces}
                  className="flex-shrink-0"
                />
                <span className="font-semibold text-lg sm:text-xl">
                  {t('memoryWorkspace.memoryWorkspace', { ns: 'common' }) as string}
                </span>
              </div>
              <div className="flex flex-shrink-0 items-center sm:ml-auto">
                <PortalButton variant="ghost">
                  {t('memoryWorkspace.returnToDashboard', { ns: 'common' }) as string}
                </PortalButton>
              </div>
            </div>

            {sectionNav && (
              <nav
                className="flex items-center gap-2 pb-3"
                aria-label={t('memoryWorkspace.sectionNavigationLabel', { ns: 'common' }) as string}
              >
                <NavLink
                  to={sectionNav.memoriesHref}
                  onClick={() => rememberWorkspaceSection('memories')}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex h-8 items-center rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-workspace-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-surface-100',
                      isActive
                        ? 'bg-workspace-primary-500 text-white shadow-sm'
                        : 'text-workspace-text-secondary hover:bg-workspace-surface-200/80 hover:text-workspace-text-primary'
                    )
                  }
                >
                  {t('memoryWorkspace.memoriesTab', { ns: 'common' }) as string}
                </NavLink>
                {sectionNav.showMap && (
                  <NavLink
                    to={sectionNav.mapHref}
                    onClick={() => rememberWorkspaceSection('map')}
                    className={({ isActive }) =>
                      cn(
                        'inline-flex h-8 items-center rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-workspace-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-surface-100',
                        isActive
                          ? 'bg-workspace-primary-500 text-white shadow-sm'
                          : 'text-workspace-text-secondary hover:bg-workspace-surface-200/80 hover:text-workspace-text-primary'
                      )
                    }
                  >
                    {t('memoryWorkspace.memoryMap', { ns: 'common' }) as string}
                  </NavLink>
                )}
              </nav>
            )}
          </div>
        </header>

        <main className={mainClassName}>{children}</main>
      </div>
    </div>
  );
}
